import React from 'react';
import jsPDF from 'jspdf';

// Colores del semáforo en RGB (7 estados)
const SEMAPHORE_COLORS_RGB = {
  green: [34, 197, 94],
  blue: [59, 130, 246],
  yellow: [234, 179, 8],
  orange: [249, 115, 22],
  red: [239, 68, 68],
  dark_gray: [107, 114, 128],
  black: [31, 41, 55]
};

// Labels del semáforo (7 estados)
const SEMAPHORE_LABELS = {
  green: 'Activo con hitos',
  blue: 'Acompañar',
  yellow: 'Activo sin hitos',
  orange: 'Sin actividad',
  red: 'En riesgo',
  dark_gray: 'Inactivo',
  black: 'No iniciado'
};

// Preview colors (hex)
const SEMAPHORE_COLORS = {
  green: '#22c55e',
  blue: '#3b82f6',
  yellow: '#eab308',
  orange: '#f97316',
  red: '#ef4444',
  dark_gray: '#6b7280',
  black: '#1f2937'
};

// =====================================================
// FUNCIÓN PRINCIPAL: Generar PDF Landscape Universal
// =====================================================
export const generatePDFDirect = (dashboardData, partnerName, comercialData, npsData, ticketsData) => {
  const data = transformDataForPDF(dashboardData, partnerName, comercialData, npsData, ticketsData);
  
  // Create PDF - LANDSCAPE A4 (297mm x 210mm)
  const doc = new jsPDF('l', 'mm', 'a4');
  
  const pageWidth = 297;
  const pageHeight = 210;
  
  // ═══════════════════════════════════════════════════════════
  // BLOQUE 1: HEADER (y: 0-15mm)
  // ═══════════════════════════════════════════════════════════
  doc.setFillColor(22, 163, 74);
  doc.rect(0, 0, pageWidth, 15, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('CAPABILIA', 10, 10);
  
  doc.setFontSize(14);
  doc.text('Persistencia 360 — ' + data.partner, pageWidth / 2, 10, { align: 'center' });
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('Informe al ' + data.fecha, pageWidth - 10, 10, { align: 'right' });
  
  // ═══════════════════════════════════════════════════════════
  // BLOQUE 2: GESTIÓN ACADÉMICA - SEMÁFORO (y: 18-75mm)
  // ═══════════════════════════════════════════════════════════
  const sem_Y = 20;
  doc.setTextColor(31, 41, 55);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Gestión Académica — Semáforo', 10, sem_Y);
  
  // 7 estados con barras (lado izquierdo)
  const maxCount = Math.max(...data.semaforo.map(s => s.count), 1);
  const rowH = 7;
  const barMaxW = 110;
  
  data.semaforo.forEach((item, i) => {
    const y = sem_Y + 8 + (i * rowH);
    const rgb = SEMAPHORE_COLORS_RGB[item.estado];
    
    // Círculo de color
    doc.setFillColor(rgb[0], rgb[1], rgb[2]);
    doc.circle(14, y, 2, 'F');
    
    // Nombre del estado
    doc.setTextColor(55, 65, 81);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(SEMAPHORE_LABELS[item.estado], 20, y + 1);
    
    // Barra de fondo gris
    doc.setFillColor(243, 244, 246);
    doc.rect(60, y - 2, barMaxW, 4, 'F');
    
    // Barra de color proporcional
    const barW = Math.max((item.count / maxCount) * barMaxW, 2);
    doc.setFillColor(rgb[0], rgb[1], rgb[2]);
    doc.rect(60, y - 2, barW, 4, 'F');
    
    // Número
    doc.setTextColor(55, 65, 81);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(item.count.toLocaleString(), 180, y + 1, { align: 'right' });
    
    // Porcentaje
    doc.setTextColor(107, 114, 128);
    doc.setFont('helvetica', 'normal');
    doc.text(item.pct + '%', 200, y + 1, { align: 'right' });
  });
  
  // Cards resumen (lado derecho)
  const summaryX = 210;
  const summaryW = 75;
  
  // Card Activos
  doc.setFillColor(236, 253, 245);
  doc.setDrawColor(167, 243, 208);
  doc.rect(summaryX, sem_Y + 3, summaryW, 22, 'FD');
  
  doc.setTextColor(34, 197, 94);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text(data.resumen.activos.toLocaleString(), summaryX + summaryW / 2, sem_Y + 14, { align: 'center' });
  doc.setFontSize(10);
  doc.text(data.resumen.activos_pct + '%', summaryX + summaryW / 2, sem_Y + 21, { align: 'center' });
  doc.setTextColor(22, 101, 52);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('Activos', summaryX + summaryW / 2, sem_Y + 25, { align: 'center' });
  
  // Card En Riesgo/Inactivos
  doc.setFillColor(254, 242, 242);
  doc.setDrawColor(254, 202, 202);
  doc.rect(summaryX, sem_Y + 28, summaryW, 22, 'FD');
  
  doc.setTextColor(239, 68, 68);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text(data.resumen.en_riesgo.toLocaleString(), summaryX + summaryW / 2, sem_Y + 39, { align: 'center' });
  doc.setFontSize(10);
  doc.text(data.resumen.en_riesgo_pct + '%', summaryX + summaryW / 2, sem_Y + 46, { align: 'center' });
  doc.setTextColor(153, 27, 27);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('En Riesgo / Inactivos', summaryX + summaryW / 2, sem_Y + 50, { align: 'center' });
  
  // ═══════════════════════════════════════════════════════════
  // BLOQUE 3: ACCESO A PLATAFORMA (y: 78-100mm) - 5 cards
  // ═══════════════════════════════════════════════════════════
  const acc_Y = 78;
  doc.setTextColor(31, 41, 55);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Acceso a Plataforma', 10, acc_Y);
  
  const cardW = 54;
  const cardH = 18;
  const cardGap = 2;
  const startX = 10;
  
  const accCards = [
    { value: data.acceso.activos_7d.toLocaleString(), label: 'Accedió esta semana', color: [34, 197, 94], pct: data.acceso.activos_7d_pct },
    { value: data.acceso.recientes_15d.toLocaleString(), label: 'Hace 7-15 días', color: [234, 179, 8], pct: data.acceso.recientes_15d_pct },
    { value: data.acceso.alejandose_30d.toLocaleString(), label: 'Hace 15-30 días', color: [249, 115, 22], pct: data.acceso.alejandose_30d_pct },
    { value: data.acceso.inactivos_30d.toLocaleString(), label: 'Hace más de 30 días', color: [239, 68, 68], pct: data.acceso.inactivos_30d_pct },
    { value: data.acceso.sin_acceso.toLocaleString(), label: 'Sin acceso registrado', color: [31, 41, 55], pct: data.acceso.sin_acceso_pct }
  ];
  
  accCards.forEach((card, i) => {
    const x = startX + i * (cardW + cardGap);
    const y = acc_Y + 4;
    
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(229, 231, 235);
    doc.rect(x, y, cardW, cardH, 'FD');
    
    // Punto de color
    doc.setFillColor(card.color[0], card.color[1], card.color[2]);
    doc.circle(x + 5, y + 6, 2.5, 'F');
    
    doc.setTextColor(31, 41, 55);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text(card.value, x + 22, y + 8, { align: 'left' });
    
    doc.setTextColor(107, 114, 128);
    doc.setFontSize(8);
    doc.text(card.pct + '%', x + cardW - 3, y + 7, { align: 'right' });
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.text(card.label, x + cardW / 2, y + 15, { align: 'center' });
  });
  
  // ═══════════════════════════════════════════════════════════
  // BLOQUE 4: RENDIMIENTO + TASAS (y: 102-145mm)
  // ═══════════════════════════════════════════════════════════
  const rend_Y = 102;
  
  // --- COLUMNA IZQUIERDA: Rendimiento Académico ---
  doc.setTextColor(31, 41, 55);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Rendimiento Académico', 10, rend_Y);
  
  const rendMaxCount = Math.max(...data.rendimiento.map(r => r.count), 1);
  
  data.rendimiento.forEach((item, i) => {
    const y = rend_Y + 10 + (i * 12);
    
    doc.setTextColor(55, 65, 81);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(item.estado, 10, y);
    
    // Barra fondo
    doc.setFillColor(243, 244, 246);
    doc.rect(10, y + 2, 100, 5, 'F');
    
    // Barra color
    const barW = Math.max((item.count / rendMaxCount) * 100, 2);
    doc.setFillColor(item.color[0], item.color[1], item.color[2]);
    doc.rect(10, y + 2, barW, 5, 'F');
    
    // Número y %
    doc.setTextColor(31, 41, 55);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(item.count.toLocaleString(), 120, y + 6, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(107, 114, 128);
    doc.setFontSize(9);
    doc.text(item.pct + '%', 140, y + 6, { align: 'right' });
  });
  
  // --- COLUMNA DERECHA: Tasas ---
  const tasas_X = 160;
  
  doc.setTextColor(31, 41, 55);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Tasas', tasas_X, rend_Y);
  
  // Card Tasa Finalización
  doc.setFillColor(236, 253, 245);
  doc.setDrawColor(167, 243, 208);
  doc.rect(tasas_X, rend_Y + 5, 60, 28, 'FD');
  
  doc.setTextColor(34, 197, 94);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(24);
  doc.text(data.tasas.finalizacion + '%', tasas_X + 30, rend_Y + 20, { align: 'center' });
  doc.setTextColor(22, 101, 52);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('Tasa de Finalización', tasas_X + 30, rend_Y + 29, { align: 'center' });
  
  // Card Tasa Abandono
  doc.setFillColor(254, 242, 242);
  doc.setDrawColor(254, 202, 202);
  doc.rect(tasas_X + 65, rend_Y + 5, 60, 28, 'FD');
  
  doc.setTextColor(239, 68, 68);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(24);
  doc.text(data.tasas.abandono + '%', tasas_X + 95, rend_Y + 20, { align: 'center' });
  doc.setTextColor(153, 27, 27);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('Tasa de Abandono', tasas_X + 95, rend_Y + 29, { align: 'center' });
  
  // ═══════════════════════════════════════════════════════════
  // BLOQUE 5: EVOLUCIÓN NPS (y: 148-195mm)
  // ═══════════════════════════════════════════════════════════
  const nps_Y = 148;
  
  doc.setTextColor(31, 41, 55);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Evolución NPS por Mes', 10, nps_Y);
  
  // NPS Score actual (lado izquierdo)
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.rect(10, nps_Y + 5, 50, 40, 'FD');
  
  const npsColor = data.nps.score >= 50 ? [22, 163, 74] : data.nps.score >= 0 ? [234, 179, 8] : [239, 68, 68];
  doc.setTextColor(npsColor[0], npsColor[1], npsColor[2]);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.text((data.nps.score >= 0 ? '+' : '') + data.nps.score, 35, nps_Y + 22, { align: 'center' });
  
  doc.setTextColor(107, 114, 128);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('NPS Actual', 35, nps_Y + 32, { align: 'center' });
  
  // Totales
  doc.setFontSize(7);
  doc.setTextColor(34, 197, 94);
  doc.text('P: ' + data.nps.promotores, 15, nps_Y + 40);
  doc.setTextColor(234, 179, 8);
  doc.text('N: ' + data.nps.neutrales, 30, nps_Y + 40);
  doc.setTextColor(239, 68, 68);
  doc.text('D: ' + data.nps.detractores, 45, nps_Y + 40);
  
  // Gráfico de barras por mes (lado derecho)
  const graphX = 70;
  const graphY = nps_Y + 8;
  const graphW = 215;
  const graphH = 35;
  
  // Fondo del gráfico
  doc.setFillColor(250, 250, 250);
  doc.rect(graphX, graphY, graphW, graphH, 'F');
  
  // Línea base 0
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.line(graphX, graphY + graphH - 5, graphX + graphW, graphY + graphH - 5);
  
  if (data.nps.historico && data.nps.historico.length > 0) {
    const barWidth = Math.min(20, (graphW - 20) / data.nps.historico.length);
    const gap = 5;
    const maxVal = Math.max(...data.nps.historico.map(h => h.promotores + h.neutrales + h.detractores), 1);
    
    data.nps.historico.slice(-12).forEach((h, i) => {
      const x = graphX + 10 + i * (barWidth + gap);
      const baseY = graphY + graphH - 5;
      
      // Calcular alturas proporcionales
      const scale = (graphH - 12) / maxVal;
      const hProm = h.promotores * scale;
      const hNeut = h.neutrales * scale;
      const hDetr = h.detractores * scale;
      
      // Barra apilada: Detractores (abajo), Neutrales (medio), Promotores (arriba)
      let currentY = baseY;
      
      // Detractores (rojo)
      if (hDetr > 0) {
        doc.setFillColor(239, 68, 68);
        doc.rect(x, currentY - hDetr, barWidth, hDetr, 'F');
        currentY -= hDetr;
      }
      
      // Neutrales (amarillo)
      if (hNeut > 0) {
        doc.setFillColor(234, 179, 8);
        doc.rect(x, currentY - hNeut, barWidth, hNeut, 'F');
        currentY -= hNeut;
      }
      
      // Promotores (verde)
      if (hProm > 0) {
        doc.setFillColor(34, 197, 94);
        doc.rect(x, currentY - hProm, barWidth, hProm, 'F');
      }
      
      // Label del mes
      doc.setTextColor(107, 114, 128);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6);
      doc.text(h.mes || '', x + barWidth / 2, baseY + 4, { align: 'center' });
    });
    
    // Leyenda
    doc.setFontSize(7);
    doc.setFillColor(34, 197, 94);
    doc.rect(graphX + graphW - 70, graphY + 2, 4, 4, 'F');
    doc.setTextColor(55, 65, 81);
    doc.text('Promotores', graphX + graphW - 64, graphY + 5);
    
    doc.setFillColor(234, 179, 8);
    doc.rect(graphX + graphW - 70, graphY + 8, 4, 4, 'F');
    doc.text('Neutrales', graphX + graphW - 64, graphY + 11);
    
    doc.setFillColor(239, 68, 68);
    doc.rect(graphX + graphW - 70, graphY + 14, 4, 4, 'F');
    doc.text('Detractores', graphX + graphW - 64, graphY + 17);
  } else {
    doc.setTextColor(150, 150, 150);
    doc.setFontSize(10);
    doc.text('Sin datos históricos de NPS', graphX + graphW / 2, graphY + graphH / 2, { align: 'center' });
  }
  
  // ═══════════════════════════════════════════════════════════
  // FOOTER
  // ═══════════════════════════════════════════════════════════
  doc.setFillColor(243, 244, 246);
  doc.rect(0, pageHeight - 8, pageWidth, 8, 'F');
  
  doc.setTextColor(107, 114, 128);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('Persistencia 360 by Capabilia · Confidencial · Generado el ' + data.fecha, pageWidth / 2, pageHeight - 3, { align: 'center' });
  
  // Guardar con nombre del partner
  const dateStr = new Date().toLocaleDateString('es-AR').replace(/\//g, '-');
  const partnerSlug = data.partner.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 20);
  doc.save('Persistencia360_' + partnerSlug + '_' + dateStr + '.pdf');
  
  return true;
};

// =====================================================
// Transform data helper
// =====================================================
const transformDataForPDF = (dashboardData, partnerName, comercialData, npsData, ticketsData) => {
  const fecha = new Date().toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' });
  
  if (!dashboardData) {
    return getDefaultData(fecha, partnerName);
  }
  
  // Semáforo (7 estados: green, blue, yellow, orange, red, dark_gray, black)
  const semaforoTotal = dashboardData.semaforo?.total || 1;
  const semaforoArray = ['green', 'blue', 'yellow', 'orange', 'red', 'dark_gray', 'black'].map(estado => ({
    estado,
    count: dashboardData.semaforo?.[estado] || 0,
    pct: ((dashboardData.semaforo?.[estado] || 0) / semaforoTotal * 100).toFixed(1)
  }));
  
  // Resumen Activos vs En Riesgo (green + blue + yellow = activos)
  const activos = (dashboardData.semaforo?.green || 0) + (dashboardData.semaforo?.blue || 0) + (dashboardData.semaforo?.yellow || 0);
  const enRiesgo = semaforoTotal - activos;
  
  // Acceso a Plataforma
  const acceso = {
    activos_7d: dashboardData.actividad?.activos_7d || 0,
    activos_7d_pct: dashboardData.actividad?.activos_7d_pct || 0,
    recientes_15d: dashboardData.actividad?.recientes_15d || 0,
    recientes_15d_pct: dashboardData.actividad?.recientes_15d_pct || 0,
    alejandose_30d: dashboardData.actividad?.alejandose_30d || 0,
    alejandose_30d_pct: dashboardData.actividad?.alejandose_30d_pct || 0,
    inactivos_30d: dashboardData.actividad?.inactivos_30d || 0,
    inactivos_30d_pct: dashboardData.actividad?.inactivos_30d_pct || 0,
    sin_acceso: dashboardData.actividad?.sin_acceso || 0,
    sin_acceso_pct: dashboardData.actividad?.sin_acceso_pct || 0
  };
  
  // Rendimiento
  const rendimientoArray = [
    { estado: 'Aprobaron (≥60%)', count: dashboardData.rendimiento?.aprobaron || 0, pct: dashboardData.rendimiento?.aprobaron_pct || 0, color: [34, 197, 94] },
    { estado: 'Sin aprobar (<60%)', count: dashboardData.rendimiento?.sin_aprobar || 0, pct: dashboardData.rendimiento?.sin_aprobar_pct || 0, color: [249, 115, 22] },
    { estado: 'Sin rendir (0%)', count: dashboardData.rendimiento?.sin_rendir || 0, pct: dashboardData.rendimiento?.sin_rendir_pct || 0, color: [107, 114, 128] }
  ];
  
  // Tasas
  const tasas = {
    finalizacion: dashboardData.tasa_finalizacion?.toFixed(1) || comercialData?.tasa_finalizacion?.toFixed(1) || '0.0',
    abandono: dashboardData.tasa_abandono?.toFixed(1) || comercialData?.tasa_abandono?.toFixed(1) || '0.0'
  };
  
  // NPS con histórico
  const nps = {
    score: npsData?.score ?? 0,
    promotores: npsData?.promotores || 0,
    neutrales: npsData?.neutrales || 0,
    detractores: npsData?.detractores || 0,
    historico: npsData?.historico || []
  };
  
  return {
    partner: partnerName || 'Partner',
    fecha,
    semaforo: semaforoArray,
    resumen: {
      activos,
      activos_pct: ((activos / semaforoTotal) * 100).toFixed(1),
      en_riesgo: enRiesgo,
      en_riesgo_pct: ((enRiesgo / semaforoTotal) * 100).toFixed(1)
    },
    acceso,
    rendimiento: rendimientoArray,
    tasas,
    nps
  };
};

const getDefaultData = (fecha, partnerName) => ({
  partner: partnerName || 'Partner',
  fecha,
  semaforo: ['green', 'blue', 'yellow', 'orange', 'red', 'dark_gray', 'black'].map(estado => ({ estado, count: 0, pct: '0.0' })),
  resumen: { activos: 0, activos_pct: '0.0', en_riesgo: 0, en_riesgo_pct: '0.0' },
  acceso: { activos_7d: 0, activos_7d_pct: 0, recientes_15d: 0, recientes_15d_pct: 0, alejandose_30d: 0, alejandose_30d_pct: 0, inactivos_30d: 0, inactivos_30d_pct: 0, sin_acceso: 0, sin_acceso_pct: 0 },
  rendimiento: [
    { estado: 'Aprobaron (≥60%)', count: 0, pct: 0, color: [34, 197, 94] },
    { estado: 'Sin aprobar (<60%)', count: 0, pct: 0, color: [249, 115, 22] },
    { estado: 'Sin rendir (0%)', count: 0, pct: 0, color: [107, 114, 128] }
  ],
  tasas: { finalizacion: '0.0', abandono: '0.0' },
  nps: { score: 0, promotores: 0, neutrales: 0, detractores: 0, historico: [] }
});

// =====================================================
// Preview Component (visual - Landscape)
// =====================================================
const PDFPreview = ({ dashboardData, partnerName, comercialData, npsData, ticketsData }) => {
  const data = transformDataForPDF(dashboardData, partnerName, comercialData, npsData, ticketsData);
  const maxSem = Math.max(...data.semaforo.map(s => s.count), 1);

  return (
    <div id="pdf-content" style={{ 
      width: '297mm', 
      height: '210mm', 
      backgroundColor: '#fff', 
      fontFamily: 'system-ui', 
      fontSize: '10px', 
      display: 'flex', 
      flexDirection: 'column', 
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      overflow: 'hidden'
    }}>
      {/* HEADER */}
      <div style={{ flex: '0 0 15mm', backgroundColor: '#16a34a', padding: '0 10mm', display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#fff' }}>
        <div style={{ fontWeight: 700, fontSize: '12pt' }}>CAPABILIA</div>
        <div style={{ fontSize: '14pt', fontWeight: 700 }}>Persistencia 360 — {data.partner}</div>
        <div style={{ fontSize: '9pt' }}>Informe al {data.fecha}</div>
      </div>

      {/* GESTIÓN ACADÉMICA - SEMÁFORO */}
      <div style={{ flex: '0 0 58mm', padding: '3mm 10mm', display: 'flex', gap: '5mm' }}>
        <div style={{ flex: 2 }}>
          <div style={{ fontWeight: 600, fontSize: '10pt', marginBottom: '3mm' }}>Gestión Académica — Semáforo</div>
          {data.semaforo.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: '2mm', gap: '3mm' }}>
              <div style={{ width: '5mm', height: '5mm', backgroundColor: SEMAPHORE_COLORS[s.estado], borderRadius: '50%' }} />
              <div style={{ width: '25mm', fontSize: '9pt' }}>{SEMAPHORE_LABELS[s.estado]}</div>
              <div style={{ flex: 1, height: '5mm', backgroundColor: '#f3f4f6', borderRadius: '2px' }}>
                <div style={{ width: `${Math.max((s.count / maxSem) * 100, 2)}%`, height: '100%', backgroundColor: SEMAPHORE_COLORS[s.estado], borderRadius: '2px' }} />
              </div>
              <div style={{ width: '15mm', textAlign: 'right', fontSize: '9pt', fontWeight: 600 }}>{s.count.toLocaleString()}</div>
              <div style={{ width: '12mm', textAlign: 'right', fontSize: '9pt', color: '#6b7280' }}>{s.pct}%</div>
            </div>
          ))}
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '3mm' }}>
          <div style={{ flex: 1, backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '6px', padding: '4mm', textAlign: 'center' }}>
            <div style={{ fontSize: '20pt', fontWeight: 700, color: '#22c55e' }}>{data.resumen.activos.toLocaleString()}</div>
            <div style={{ fontSize: '10pt', fontWeight: 600, color: '#22c55e' }}>{data.resumen.activos_pct}%</div>
            <div style={{ fontSize: '8pt', color: '#166534' }}>Activos</div>
          </div>
          <div style={{ flex: 1, backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '4mm', textAlign: 'center' }}>
            <div style={{ fontSize: '20pt', fontWeight: 700, color: '#ef4444' }}>{data.resumen.en_riesgo.toLocaleString()}</div>
            <div style={{ fontSize: '10pt', fontWeight: 600, color: '#ef4444' }}>{data.resumen.en_riesgo_pct}%</div>
            <div style={{ fontSize: '8pt', color: '#991b1b' }}>En Riesgo / Inactivos</div>
          </div>
        </div>
      </div>

      {/* ACCESO A PLATAFORMA */}
      <div style={{ flex: '0 0 25mm', padding: '2mm 10mm' }}>
        <div style={{ fontWeight: 600, fontSize: '10pt', marginBottom: '3mm' }}>Acceso a Plataforma</div>
        <div style={{ display: 'flex', gap: '2mm' }}>
          {[
            { v: data.acceso.activos_7d, l: 'Accedió esta semana', c: '#22c55e', p: data.acceso.activos_7d_pct },
            { v: data.acceso.recientes_15d, l: 'Hace 7-15 días', c: '#eab308', p: data.acceso.recientes_15d_pct },
            { v: data.acceso.alejandose_30d, l: 'Hace 15-30 días', c: '#f97316', p: data.acceso.alejandose_30d_pct },
            { v: data.acceso.inactivos_30d, l: 'Hace más de 30 días', c: '#ef4444', p: data.acceso.inactivos_30d_pct },
            { v: data.acceso.sin_acceso, l: 'Sin acceso registrado', c: '#1f2937', p: data.acceso.sin_acceso_pct }
          ].map((k, i) => (
            <div key={i} style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: '4px', padding: '2mm', display: 'flex', alignItems: 'center', gap: '2mm' }}>
              <div style={{ width: '5mm', height: '5mm', backgroundColor: k.c, borderRadius: '50%' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '14pt', fontWeight: 700 }}>{k.v.toLocaleString()} <span style={{ fontSize: '9pt', color: '#6b7280', fontWeight: 400 }}>{k.p}%</span></div>
                <div style={{ fontSize: '7pt', color: '#6b7280' }}>{k.l}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* RENDIMIENTO + TASAS */}
      <div style={{ flex: '0 0 42mm', padding: '2mm 10mm', display: 'flex', gap: '10mm' }}>
        {/* Rendimiento */}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: '10pt', marginBottom: '3mm' }}>Rendimiento Académico</div>
          {data.rendimiento.map((r, i) => (
            <div key={i} style={{ marginBottom: '3mm' }}>
              <div style={{ display: 'flex', fontSize: '9pt', alignItems: 'center', gap: '3mm' }}>
                <div style={{ width: '30mm' }}>{r.estado}</div>
                <div style={{ flex: 1, height: '6mm', backgroundColor: '#f3f4f6', borderRadius: '3px' }}>
                  <div style={{ width: `${r.pct}%`, height: '100%', backgroundColor: `rgb(${r.color.join(',')})`, borderRadius: '3px' }} />
                </div>
                <div style={{ width: '15mm', textAlign: 'right', fontWeight: 600 }}>{r.count.toLocaleString()}</div>
                <div style={{ width: '12mm', textAlign: 'right', color: '#6b7280' }}>{r.pct}%</div>
              </div>
            </div>
          ))}
        </div>
        {/* Tasas */}
        <div style={{ width: '130mm' }}>
          <div style={{ fontWeight: 600, fontSize: '10pt', marginBottom: '3mm' }}>Tasas</div>
          <div style={{ display: 'flex', gap: '5mm' }}>
            <div style={{ flex: 1, backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '6px', padding: '4mm', textAlign: 'center' }}>
              <div style={{ fontSize: '24pt', fontWeight: 700, color: '#22c55e' }}>{data.tasas.finalizacion}%</div>
              <div style={{ fontSize: '9pt', color: '#166534' }}>Tasa de Finalización</div>
            </div>
            <div style={{ flex: 1, backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '4mm', textAlign: 'center' }}>
              <div style={{ fontSize: '24pt', fontWeight: 700, color: '#ef4444' }}>{data.tasas.abandono}%</div>
              <div style={{ fontSize: '9pt', color: '#991b1b' }}>Tasa de Abandono</div>
            </div>
          </div>
        </div>
      </div>

      {/* EVOLUCIÓN NPS */}
      <div style={{ flex: '0 0 55mm', padding: '2mm 10mm', display: 'flex', gap: '5mm' }}>
        {/* NPS Score */}
        <div style={{ width: '50mm', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '4mm', textAlign: 'center' }}>
          <div style={{ fontWeight: 600, fontSize: '10pt', marginBottom: '2mm' }}>NPS Actual</div>
          <div style={{ fontSize: '32pt', fontWeight: 700, color: data.nps.score >= 50 ? '#16a34a' : data.nps.score >= 0 ? '#eab308' : '#ef4444' }}>
            {data.nps.score >= 0 ? '+' : ''}{data.nps.score}
          </div>
          <div style={{ fontSize: '8pt', marginTop: '2mm', display: 'flex', justifyContent: 'center', gap: '3mm' }}>
            <span style={{ color: '#22c55e' }}>P: {data.nps.promotores}</span>
            <span style={{ color: '#eab308' }}>N: {data.nps.neutrales}</span>
            <span style={{ color: '#ef4444' }}>D: {data.nps.detractores}</span>
          </div>
        </div>
        {/* Gráfico evolución */}
        <div style={{ flex: 1, backgroundColor: '#fafafa', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '3mm' }}>
          <div style={{ fontWeight: 600, fontSize: '10pt', marginBottom: '2mm' }}>Evolución NPS por Mes</div>
          {data.nps.historico && data.nps.historico.length > 0 ? (
            <div style={{ display: 'flex', alignItems: 'flex-end', height: '35mm', gap: '2mm', paddingTop: '2mm' }}>
              {data.nps.historico.slice(-12).map((h, i) => {
                const maxH = Math.max(...data.nps.historico.map(x => x.promotores + x.neutrales + x.detractores), 1);
                const scale = 28 / maxH;
                return (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <div style={{ height: `${h.promotores * scale}mm`, backgroundColor: '#22c55e', borderRadius: '2px 2px 0 0' }} />
                      <div style={{ height: `${h.neutrales * scale}mm`, backgroundColor: '#eab308' }} />
                      <div style={{ height: `${h.detractores * scale}mm`, backgroundColor: '#ef4444', borderRadius: '0 0 2px 2px' }} />
                    </div>
                    <div style={{ fontSize: '6pt', color: '#6b7280', marginTop: '1mm' }}>{h.mes}</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ height: '35mm', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
              Sin datos históricos de NPS
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '4mm', fontSize: '7pt', marginTop: '1mm' }}>
            <span><span style={{ color: '#22c55e' }}>●</span> Promotores</span>
            <span><span style={{ color: '#eab308' }}>●</span> Neutrales</span>
            <span><span style={{ color: '#ef4444' }}>●</span> Detractores</span>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div style={{ flex: '0 0 8mm', backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '7pt', color: '#6b7280' }}>
        Persistencia 360 by Capabilia · Confidencial · Generado el {data.fecha}
      </div>
    </div>
  );
};

export default PDFPreview;
